import React, { useMemo, useRef } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { ACCENT, ACCENT_BRIGHT, ACCENT_TINT, accentAlpha } from '@/components/Landing/landingTokens';
import { isDarkTheme } from '@/constants/Colors';
import { safeAccessibilityRole } from '@/constants/utils/a11y';

const isWeb = Platform.OS === 'web';

const FACTS = [
  { icon: 'bullseye', titleKey: 'pages.agents.intro.fact1Title', bodyKey: 'pages.agents.intro.fact1Body' },
  { icon: 'comments', titleKey: 'pages.agents.intro.fact2Title', bodyKey: 'pages.agents.intro.fact2Body' },
  { icon: 'wand-magic-sparkles', titleKey: 'pages.agents.intro.fact3Title', bodyKey: 'pages.agents.intro.fact3Body' },
];

function AgentsIntroHero({ onCreate, onDiscoveryY, personalContent, search, children }) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const compact = isXS || isSM;
  const discoveryY = useRef(0);
  const dark = isDarkTheme(theme);
  const brand = dark ? ACCENT_TINT : ACCENT;
  const brandFill = dark ? ACCENT_BRIGHT : ACCENT;
  const styles = useMemo(() => createStyles(colors, compact, dark), [colors, compact, dark]);

  return (
    <View style={styles.wrap}>
      <View style={styles.hero} accessibilityRole={safeAccessibilityRole('banner')}>
        <View style={styles.heroGrid}>
          <View style={styles.intro}>
            <View style={styles.eyebrow}>
              <View style={styles.eyebrowMark}>
                <Icon name="robot" size={14} color={brand} />
              </View>
              <Text size="xs" weight="semibold" style={[styles.eyebrowText, { color: brand }]}>
                {t('pages.agents.intro.headline')}
              </Text>
            </View>
            <Text tag="h1" weight="extraBold" style={styles.headline}>{t('navigation.agents')}</Text>
            <Text tag="p" color="secondary" style={styles.lead}>{t('pages.agents.intro.lead')}</Text>
            <View style={styles.heroActions}>
              <Button
                text={t('pages.agents.intro.createCta')}
                prependIcon="plus"
                variant="purple"
                size="large"
                textWeight="semibold"
                onPress={onCreate}
                style={{ backgroundColor: brandFill }}
                hoverBackgroundColor={dark ? '#2A8558' : ACCENT_BRIGHT}
              />
              <Text size="s" color="tertiary">{t('pages.agents.intro.createSubtitle')}</Text>
            </View>
          </View>

          <View style={styles.facts} accessibilityRole={safeAccessibilityRole('list')}>
            {FACTS.map((fact, index) => (
              <View key={fact.titleKey} style={[styles.fact, index === 0 && styles.factFirst]} accessibilityRole={safeAccessibilityRole('listitem')}>
                <View style={styles.factIcon}>
                  <Icon name={fact.icon} size={15} color={brand} />
                </View>
                <View style={styles.factCopy}>
                  <Text size="s" weight="semibold">{t(fact.titleKey)}</Text>
                  <Text size="s" color="secondary" style={styles.factBody}>{t(fact.bodyKey)}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>

      {personalContent}

      <View style={styles.discover} onLayout={(event) => {
        const y = event.nativeEvent.layout.y;
        discoveryY.current = y;
        onDiscoveryY?.(y);
      }}>
        <View style={styles.discoverHead}>
          <Text tag="h2" size="xl" weight="bold">{t('pages.agents.intro.browseTitle')}</Text>
          <Text size="n" color="secondary">{t('pages.agents.intro.browseSubtitle')}</Text>
        </View>
        <View style={styles.search}>
          <Icon name="search" size={16} color={colors.text_color_secondary} />
          <TextInput
            value={search.value ?? ''}
            onChangeText={search.onChangeText}
            placeholder={search.placeholder}
            placeholderTextColor={colors.text_color_tertiary}
            style={styles.searchInput}
            autoCorrect={false}
            autoCapitalize="none"
            accessibilityLabel={search.placeholder}
          />
          {!!search.value && (
            <Pressable
              onPress={() => search.onChangeText('')}
              accessibilityRole="button"
              accessibilityLabel={t('common.clear')}
              hitSlop={8}
              style={styles.clear}
            >
              <Icon name="xmark" size={14} color={colors.text_color_secondary} />
            </Pressable>
          )}
        </View>
        {children}
      </View>
    </View>
  );
}

AgentsIntroHero.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onDiscoveryY: PropTypes.func,
  personalContent: PropTypes.node,
  search: PropTypes.shape({
    value: PropTypes.string,
    onChangeText: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
  }).isRequired,
  children: PropTypes.node,
};

const createStyles = (colors, compact, dark) => StyleSheet.create({
  wrap: { width: '100%', paddingHorizontal: compact ? 16 : 24, paddingTop: compact ? 20 : 28 },
  hero: {
    borderWidth: 1,
    borderColor: accentAlpha(dark ? 0.28 : 0.12, dark),
    backgroundColor: accentAlpha(dark ? 0.12 : 0.055, dark),
    borderRadius: 24,
    padding: compact ? 22 : 32,
    marginBottom: compact ? 28 : 36,
  },
  heroGrid: {
    flexDirection: compact ? 'column' : 'row',
    alignItems: compact ? 'stretch' : 'center',
    gap: compact ? 28 : 48,
  },
  // The stacked layout must measure content height, not divide it with flex.
  intro: { flex: compact ? undefined : 1.15, minWidth: 0, gap: 14, maxWidth: compact ? '100%' : 560 },
  eyebrow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  eyebrowMark: {
    width: 28,
    height: 28,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: accentAlpha(dark ? 0.22 : 0.10, dark),
  },
  eyebrowText: { flexShrink: 1, minWidth: 0, letterSpacing: 0.4 },
  headline: { fontSize: compact ? 36 : 52, lineHeight: compact ? 40 : 56, letterSpacing: -1.8 },
  lead: { fontSize: 16, lineHeight: 25, maxWidth: 520 },
  heroActions: {
    flexDirection: compact ? 'column' : 'row',
    flexWrap: 'wrap',
    alignItems: compact ? 'flex-start' : 'center',
    gap: compact ? 10 : 16,
    paddingTop: 6,
  },
  facts: {
    flex: compact ? undefined : 1,
    minWidth: 0,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    borderRadius: 18,
    overflow: 'hidden',
  },
  fact: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    paddingHorizontal: compact ? 16 : 18,
    paddingVertical: compact ? 14 : 16,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
  },
  factFirst: { borderTopWidth: 0 },
  factIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: accentAlpha(dark ? 0.20 : 0.09, dark),
    marginTop: 1,
  },
  factCopy: { flex: 1, minWidth: 0, gap: 4 },
  factBody: { lineHeight: 20 },
  discover: { marginTop: compact ? 32 : 40, paddingTop: compact ? 28 : 32, borderTopWidth: 1, borderTopColor: colors.border_color_secondary, gap: 18, paddingBottom: 4 },
  discoverHead: { gap: 6 },
  search: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, minHeight: 48, borderRadius: 14, borderWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
  searchInput: { flex: 1, minWidth: 0, color: colors.text_color_primary, fontSize: 15, paddingVertical: 12, ...(isWeb ? { outlineStyle: 'none' } : {}) },
  clear: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
});

export default React.memo(AgentsIntroHero);
